import { useCallback, useEffect, useState } from 'react';
import {
  arePythonTreeSitterBindingsHydratedForCurrentReady,
  isPythonTreeSitterBindingsHydrateFailed,
  isPythonTreeSitterReady,
  prewarmPythonTreeSitter,
  requestPythonTreeSitterBindingsHydrate,
  subscribePythonTreeSitterBindingsHydrated,
  subscribePythonTreeSitterReadyTransition,
} from '../../utils/pythonTreeSitterInputBindings';

export type PythonTreeSitterReadyStatus = {
  /** Tree-sitter WASM + queries are ready. */
  ready: boolean;
  /**
   * WASM ready and Control bindings have been hydrated for the current ready
   * transition (including after Retry). Gate Add/Edit/Delete on this.
   */
  mappingsReady: boolean;
  /** Init / retry / post-ready hydrate is in flight. */
  loading: boolean;
  /** WASM init or binding hydrate failed; mutations stay fail-closed until retry succeeds. */
  failed: boolean;
  /** Re-run prewarm and request a bindings re-hydrate (same coalesced init as page mount). */
  retry: () => void;
};

/**
 * Reactive Tree-sitter readiness for Control UI (Controller / Drivebase / mapping list).
 * Coalesces with page-level `prewarmPythonTreeSitter`; does not wait on LSP.
 * Mutations stay gated until the page's post-ready hardware parse successfully hydrates.
 */
export function usePythonTreeSitterReady(): PythonTreeSitterReadyStatus {
  const [ready, setReady] = useState(() => isPythonTreeSitterReady());
  const [bindingsHydrated, setBindingsHydrated] = useState(() => arePythonTreeSitterBindingsHydratedForCurrentReady());
  const [wasmFailed, setWasmFailed] = useState(false);
  const [hydrateFailed, setHydrateFailed] = useState(() => isPythonTreeSitterBindingsHydrateFailed());
  const [attempt, setAttempt] = useState(0);

  const syncHydrateState = useCallback(() => {
    setBindingsHydrated(arePythonTreeSitterBindingsHydratedForCurrentReady());
    setHydrateFailed(isPythonTreeSitterBindingsHydrateFailed());
  }, []);

  useEffect(() => {
    return subscribePythonTreeSitterReadyTransition(() => {
      setReady(isPythonTreeSitterReady());
      syncHydrateState();
    });
  }, [syncHydrateState]);

  useEffect(() => {
    return subscribePythonTreeSitterBindingsHydrated(() => {
      syncHydrateState();
    });
  }, [syncHydrateState]);

  useEffect(() => {
    if (isPythonTreeSitterReady()) {
      setReady(true);
      setWasmFailed(false);
      // Retry (attempt > 0) must re-request hydrate even when WASM is already warm
      // (e.g. parse-side init succeeded after page prewarm exhausted).
      if (attempt > 0) {
        requestPythonTreeSitterBindingsHydrate();
      }
      syncHydrateState();
      return;
    }

    let cancelled = false;
    setReady(false);
    setWasmFailed(false);
    setBindingsHydrated(false);
    setHydrateFailed(false);

    void Promise.resolve(prewarmPythonTreeSitter()).then((ok) => {
      if (cancelled) return;
      setReady(ok);
      setWasmFailed(!ok);
      if (ok) {
        requestPythonTreeSitterBindingsHydrate();
        syncHydrateState();
      } else {
        setBindingsHydrated(false);
        setHydrateFailed(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [attempt, syncHydrateState]);

  const retry = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  const failed = wasmFailed || hydrateFailed;
  const mappingsReady = ready && bindingsHydrated && !hydrateFailed;

  return {
    ready,
    mappingsReady,
    // Spinner covers WASM init and in-flight hydrate; failed (WASM or hydrate) shows Retry.
    loading: !failed && !mappingsReady,
    failed,
    retry,
  };
}

/**
 * Resolve Control mutation readiness.
 *
 * When the parent passes `mappingsReady`, that wins (config tabs / tests).
 * When omitted, fall back to live Tree-sitter + post-ready hydrate readiness so a
 * forgotten prop cannot re-enable Save/Delete while WASM is loading, after init
 * failed, or before the current-source parse has successfully hydrated.
 */
export function useResolvedMappingsReady(mappingsReady?: boolean): boolean {
  const status = usePythonTreeSitterReady();
  return mappingsReady ?? status.mappingsReady;
}
